// Medical Records → Request Services
//
// A single page that lets a records clerk request labs, scans, or other
// services on behalf of a patient. The three categories live as tabs so the
// clerk only sees one catalogue at a time:
//
//   • Labs            → POST /requestLab     (type: 'Lab')
//   • Scans / Imaging → POST /requestLab     (type: 'Scan')
//   • Other Services  → POST /requestOtherService
//
// All three categories follow the existing billing flow:
//   - amount > 0  → request lands in Accounts as a pending charge. The
//                   department-side panel (lab / scan / billing) only sees
//                   it AFTER cashier settles the payment.
//   - amount = 0  → insured / free, both the charge row AND the worklist
//                   row are written immediately by the backend, so the
//                   request surfaces on the department panel right away.
//
// No bypassing — the records clerk is just a different originator of an
// otherwise identical request.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment, Button, IconButton,
  Avatar, Chip, Divider, Stack, Grid, Card, CardActionArea, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, CircularProgress, Snackbar, Alert,
  Tooltip, Tabs, Tab, useTheme, Dialog, DialogContent, DialogTitle, DialogActions, Autocomplete,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Science as LabIcon,
  Scanner as ScanIcon,
  RoomService as ServiceIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Send as SendIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  LocalHospital as HospitalIcon,
  AssignmentTurnedIn as RequestIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  MedicalServices as DoctorIcon,
  DirectionsWalk as WalkInIcon,
  EditOutlined as EditDoctorIcon,
} from '@mui/icons-material';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';

import {
  loadAllPatientsToday, loadAllInPatients,
  loadLabdb, loadAvailableScansdb, loadOtherServicesdb,
  requestLab, requestOtherService,
  loadRequestedLabsInpatients, loadRequestedScanInpatient, loadRequestedOtherServicesInpatient,
  deleteRequestedLabdb, deleteOrReverseOtherServicedb,
  onboardedDoctorsListUrl,
  changeRequestDoctorUrl,
} from '../BaseURL';
import { formatWithOutSom } from '../Message';
import { computeAge } from '../../utils/age';
import { isNonInsured } from '../../utils/insuranceRules';

// ── Tab metadata. Centralised so the catalogue/duplicate-guard/payload code
// can stay tab-agnostic — pick the active config, pass it to the shared
// table component, and the right endpoint + accent colour fall out. ──
const TABS = {
  lab: {
    key: 'lab',
    label: 'Labs',
    icon: <LabIcon />,
    accent: '#11998e',     // teal — matches Lab pages
    accentSoft: 'rgba(17,153,142,0.16)',
    columnLabel: 'Lab Test',
    searchPlaceholder: 'Search lab tests…',
    emptyLabel: 'No lab tests configured for this patient\'s insurance.',
    requestSuccessSuffix: 'sent to Accounts for payment.',
    requestFreeSuffix: 'requested — patient will appear in Lab > My Patients shortly.',
  },
  scan: {
    key: 'scan',
    label: 'Scans / Imaging',
    icon: <ScanIcon />,
    accent: '#8b5cf6',     // violet — matches Scan pages
    accentSoft: 'rgba(139,92,246,0.16)',
    columnLabel: 'Scan',
    searchPlaceholder: 'Search scans…',
    emptyLabel: 'No scans configured for this patient\'s insurance.',
    requestSuccessSuffix: 'sent to Accounts for payment.',
    requestFreeSuffix: 'requested — patient will appear on the Scan Dashboard shortly.',
  },
  other: {
    key: 'other',
    label: 'Other Services',
    icon: <ServiceIcon />,
    accent: '#f59e0b',     // amber — neutral non-clinical accent
    accentSoft: 'rgba(245,158,11,0.18)',
    columnLabel: 'Service',
    searchPlaceholder: 'Search services…',
    emptyLabel: 'No manual services configured for this facility.',
    requestSuccessSuffix: 'recorded against this patient.',
    requestFreeSuffix: 'recorded against this patient.',
  },
};

// Insurer tariff for a catalogue row — what goes on the claim.
const claimTariff = (tab, item) =>
  Number(tab === 'scan' ? item?.tariff : (item?.base_cost ?? item?.cost)) || 0;

const FILTERS = [
  { value: 'outpatient', label: 'Outpatients' },
  { value: 'inpatient',  label: 'Inpatients'  },
];

// Accepts an optional `patient` prop. When provided, the patient picker is
// skipped — the page jumps straight into the catalogue for that patient.
// This is what makes the per-row "Request" action button on the Records
// list work without re-selecting a patient. When no patient is provided
// (e.g. invoked as a top-level menu item), the picker is shown as before.
export default function RequestServices({ patient: presetPatient = null, onClose } = {}) {
  // ── Theme tokens — same shape used by RequestLab / RequestScan so the
  // three Request pages feel like a family even though one is per-page
  // dark, one per-page violet, and this one carries all three tones at
  // once via the tab switcher. ──
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const t = useMemo(() => ({
    isDark,
    pageBg:       isDark ? '#000000' : '#F4F6FA',
    surface:      isDark ? '#0E0E14' : '#FFFFFF',
    surfaceAlt:   isDark ? '#14141C' : '#F8FAFC',
    fieldBg:      isDark ? '#14141C' : '#FFFFFF',
    borderSubtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
    borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.14)',
    text:         isDark ? '#FFFFFF' : '#0F172A',
    textMuted:    isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.6)',
    textDim:      isDark ? 'rgba(255,255,255,0.42)' : 'rgba(15,23,42,0.4)',
    rowHover:     isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
    accent: {
      green:   '#22c55e',
      emerald: '#10b981',
      blue:    '#3b82f6',
      amber:   '#f59e0b',
      danger:  '#ef4444',
    },
  }), [isDark]);

  const today = moment().format('YYYY-MM-DD');

  // ── Patient list state ────────────────────────────────────────────────
  const [filter, setFilter]                   = useState('outpatient');
  const [patients, setPatients]               = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientSearch, setPatientSearch]     = useState('');

  // ── Selected patient + active tab workspace ──────────────────────────
  // Seeded from the optional `presetPatient` prop so a Request button on
  // the patient row can jump straight into the catalogue.
  const [selectedPatient, setSelectedPatient] = useState(presetPatient);
  const [activeTab, setActiveTab]             = useState('lab');

  // Per-tab catalogue + search. Stored as a map so switching tabs doesn't
  // lose what was loaded for the others (no spinner flash on tab change).
  const [catalogue, setCatalogue]   = useState({ lab: [], scan: [], other: [] });
  const [catLoading, setCatLoading] = useState({ lab: false, scan: false, other: false });
  const [searches, setSearches]     = useState({ lab: '', scan: '', other: '' });
  const [requestingId, setRequestingId] = useState(null);

  // Existing-request lookup (one per tab) used by the duplicate guard.
  const [existing, setExisting]         = useState({ lab: [], scan: [], other: [] });
  const [sessionRequests, setSessionRequests] = useState([]); // tagged with tab key

  const [snackbar, setSnackbar]       = useState({ open: false, message: '', severity: 'success' });
  const [currentUser, setCurrentUser] = useState('');

  // Referring / requesting clinician — the onboarded doctor (or "Walk-in")
  // the patient came in under. Every request that fires while this is set
  // is tagged with this name on `doc_name`, which is what the commission /
  // discount reports key off later.
  //
  // Modelled as an option object so the Autocomplete and the payload code
  // can share the same shape. The literal `WALK_IN` constant gives us a
  // single, stable identity for the "no specific referrer" path.
  const WALK_IN = useMemo(() => ({ id: '__walkin__', name: 'Walk-in', _walkin: true }), []);
  const [doctorOptions, setDoctorOptions] = useState([WALK_IN]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [referringDoctor, setReferringDoctor] = useState(null);

  // ── Pull logged-in user so the audit + doctor/requ_by columns get the
  //    actual clerk's name instead of anonymous. ──
  useEffect(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) {
        const u = JSON.parse(raw);
        setCurrentUser(u?.name || u?.username || '');
      }
    } catch (_) { /* non-fatal */ }
  }, []);

  // ── Patient list loader ───────────────────────────────────────────────
  // ── Onboarded doctors loader ─────────────────────────────────────────
  // Loaded once per mount; "Walk-in" is pinned to the top so the clerk
  // doesn't have to scroll for the common case.
  const loadDoctors = async () => {
    setDoctorsLoading(true);
    try {
      const res = await axios.get(onboardedDoctorsListUrl, { params: { status: 'active' }, timeout: 12000 });
      const rows = res.data?.success ? (res.data.results || []) : [];
      setDoctorOptions([WALK_IN, ...rows]);
    } catch (err) {
      console.error('Failed to load onboarded doctors:', err.message);
      setDoctorOptions([WALK_IN]);
    } finally {
      setDoctorsLoading(false);
    }
  };
  useEffect(() => { loadDoctors(); /* eslint-disable-next-line */ }, []);

  const loadPatients = async () => {
    setPatientsLoading(true);
    try {
      const endpoint = filter === 'inpatient' ? loadAllInPatients : loadAllPatientsToday;
      const res = await axios.get(endpoint);
      setPatients(res.data?.success && Array.isArray(res.data.results) ? res.data.results : []);
    } catch (err) {
      console.error('Failed to load patients:', err);
      setPatients([]);
    } finally {
      setPatientsLoading(false);
    }
  };
  useEffect(() => {
    // Only fetch the patient list when we don't already have one. The
    // dialog path supplies a preset patient and never needs the picker.
    if (!selectedPatient && !presetPatient) loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selectedPatient, presetPatient]);

  // When mounted with a preset patient (dialog flow), load the catalogues
  // and the existing-today list right away so the user lands on a fully
  // rendered table instead of an empty spinner.
  useEffect(() => {
    if (!presetPatient) return;
    loadLabCatalogue(presetPatient);
    loadScanCatalogue(presetPatient);
    loadOtherCatalogue();
    loadExistingForPatient(presetPatient);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetPatient?.id]);

  // The patient table holds two insurance-related columns that are very
  // easy to confuse:
  //   • `Insurance` (capital I) → the membership / policy NUMBER
  //   • `type`                   → the scheme NAME (NHIS, Non Insured, etc.)
  // The lab/scan catalogues key prices off the scheme NAME (the `status`
  // column in lab_prices / scan_price), so we MUST send the scheme name —
  // sending the policy number returns an empty catalogue every time.
  // /loadAllPatientsToday already aliases `type` → `insurance` for the
  // patient-picker flow, while /loadPatients (Records list) exposes the
  // raw `type` column. We fall through to "Non Insured" so cash-pay
  // patients still get a populated catalogue priced at the uninsured tariff.
  const getInsurance = (p) =>
    p?.insurance || p?.type || 'Non Insured';

  // Insurance membership number — different from the scheme. Displayed
  // separately on the patient header so the clerk can read both at once.
  const getInsuranceNo = (p) => p?.Insurance || p?.membershipNo || '';

  // ── Insured patients go through the claim, not Accounts ──────────────
  // For any scheme other than Non Insured, a lab or scan is requested at
  // amount 0 with the insurer's tariff as base_cost. The backend then writes
  // the investigation (claim) row and the worklist row straight away — the
  // same path a fully-covered item already took. The patient's top-up is
  // not collected. Items with no insurer tariff (claim price 0) still go to
  // Accounts, since there's nothing to claim. Other Services are a cash-only
  // Non Insured list, so they always go to Accounts.
  const patientIsInsured = !isNonInsured(getInsurance(selectedPatient));
  const routeToClaim = (tab, item) =>
    patientIsInsured && (tab === 'lab' || tab === 'scan') && claimTariff(tab, item) > 0;

  // ── Catalogue loaders — one per tab. Each is gated on `insurance` for
  // lab + scan since the price varies by scheme; "other" is flat. ──
  const loadLabCatalogue = async (patient) => {
    setCatLoading(s => ({ ...s, lab: true }));
    try {
      const res = await axios.get(loadLabdb, { params: { insurance: getInsurance(patient) } });
      setCatalogue(c => ({ ...c, lab: res.data?.success ? (res.data.results || []) : [] }));
    } catch (_) { setCatalogue(c => ({ ...c, lab: [] })); }
    finally { setCatLoading(s => ({ ...s, lab: false })); }
  };
  const loadScanCatalogue = async (patient) => {
    setCatLoading(s => ({ ...s, scan: true }));
    try {
      const res = await axios.get(loadAvailableScansdb, { params: { insurance: getInsurance(patient) } });
      setCatalogue(c => ({ ...c, scan: res.data?.success ? (res.data.results || []) : [] }));
    } catch (_) { setCatalogue(c => ({ ...c, scan: [] })); }
    finally { setCatLoading(s => ({ ...s, scan: false })); }
  };
  const loadOtherCatalogue = async () => {
    setCatLoading(s => ({ ...s, other: true }));
    try {
      const res = await axios.get(loadOtherServicesdb, { params: { charge_status: 'Manual' } });
      setCatalogue(c => ({ ...c, other: res.data?.success ? (res.data.results || []) : [] }));
    } catch (_) { setCatalogue(c => ({ ...c, other: [] })); }
    finally { setCatLoading(s => ({ ...s, other: false })); }
  };

  // ── Existing-today loaders, used by the duplicate guard ──────────────
  const loadExistingForPatient = async (patient) => {
    try {
      const [lab, scan, other] = await Promise.all([
        axios.get(loadRequestedLabsInpatients,         { params: { patientId: patient.id, dat: today, currentDate: today } }),
        axios.get(loadRequestedScanInpatient,          { params: { patientId: patient.id, dat: today, currentDate: today } }),
        axios.get(loadRequestedOtherServicesInpatient, { params: { patientId: patient.id, dat: today, currentDate: today } }),
      ]);
      setExisting({
        lab:   lab.data?.success   ? (lab.data.results || [])   : [],
        scan:  scan.data?.success  ? (scan.data.results || [])  : [],
        other: other.data?.success ? (other.data.results || []) : [],
      });
    } catch (_) { setExisting({ lab: [], scan: [], other: [] }); }
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setActiveTab('lab');
    setSearches({ lab: '', scan: '', other: '' });
    setSessionRequests([]);
    loadLabCatalogue(patient);
    loadScanCatalogue(patient);
    loadOtherCatalogue();
    loadExistingForPatient(patient);
  };

  const handleBackToList = () => {
    setSelectedPatient(null);
    setCatalogue({ lab: [], scan: [], other: [] });
    setExisting({ lab: [], scan: [], other: [] });
    setSessionRequests([]);
    setSearches({ lab: '', scan: '', other: '' });
  };

  // ── Request a single item from any tab ───────────────────────────────
  const handleRequest = async (item, rowKey) => {
    if (!selectedPatient) return;
    const cfg = TABS[activeTab];

    // Referring doctor is required. Even a walk-in patient picks the
    // pinned "Walk-in" option explicitly — that way every request row
    // has a deterministic doc_name and there are no "" entries that
    // commission reports have to special-case.
    if (!referringDoctor) {
      setSnackbar({
        open: true, severity: 'error',
        message: 'Select a referring doctor (or "Walk-in") before requesting.',
      });
      return;
    }

    // Duplicate guard — same item today → confirm before re-submitting so
    // the clerk doesn't accidentally double-bill an insured patient.
    const itemName = item.name;
    const dupServer = (existing[activeTab] || []).some(r =>
      (r.lab || r.service) === itemName &&
      (r.date ? String(r.date).slice(0, 10) === today : true)
    );
    const dupSession = sessionRequests.some(r => r.tab === activeTab && r.itemName === itemName);
    if (dupServer || dupSession) {
      const result = await Swal.fire({
        title: 'Already requested today',
        text: `${itemName} has already been requested for ${selectedPatient.fullName} today. Request again?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, request again',
        cancelButtonText: 'Cancel',
        confirmButtonColor: cfg.accent,
        cancelButtonColor: t.accent.danger,
      });
      if (!result.isConfirmed) return;
    }

    setRequestingId(rowKey);
    try {
      let ok = false;
      let serverMsg = '';
      let reqNo = null;            // backend-generated tracking ID (LAB-/SCN-/SRV-)
      const toClaim = routeToClaim(activeTab, item);
      const chargeAmount = toClaim ? 0 : item.amount;

      if (activeTab === 'lab' || activeTab === 'scan') {
        // /requestLab handles both labs and scans — the backend branches
        // on `type` to decide which department picks up the row. We map
        // `tariff` → base_cost for scans because the catalogue returns
        // the field under a different name.
        const res = await axios.post(requestLab, {
          patientId: selectedPatient.id,
          dat: today,
          patientInsurance: getInsurance(selectedPatient),
          labName: itemName,
          amount: chargeAmount,                     // 0 for insured → claim + worklist, no Accounts
          base_cost: activeTab === 'lab' ? item.base_cost : item.tariff,
          code: item.code,
          currentUser,
          type: activeTab === 'lab' ? 'Lab' : 'Scan',
          referringDoctor: referringDoctor.name,    // doc_name on the row
        });
        ok = !!res.data?.success;
        serverMsg = res.data?.message || '';
        reqNo = res.data?.reqNo || null;
      } else {
        // Other services hit their own endpoint and use a different
        // duplicate-detection shape on the backend (it returns success:true
        // with a "Charge already recorded" message instead of failing).
        const res = await axios.post(requestOtherService, {
          patientId: selectedPatient.id,
          name: selectedPatient.fullName,
          department: 'Medical Records',
          charge: itemName,
          amount: item.amount,
          dat: today,
          doc_name: referringDoctor.name,         // referring doctor or Walk-in
          from_user: currentUser,                 // who actually keyed it
        });
        ok = !!res.data?.success;
        serverMsg = res.data?.message || '';
        reqNo = res.data?.reqNo || null;
      }

      if (ok) {
        setSessionRequests(prev => [
          ...prev,
          {
            tab: activeTab,
            itemName,
            amount: chargeAmount,
            insured: Number(chargeAmount) === 0,
            claimed: toClaim,
            referringDoctor: referringDoctor?.name || '—',
            reqNo,                                    // tracking ID for the barcode
            at: new Date().toISOString(),
          },
        ]);
        setSnackbar({
          open: true,
          severity: 'success',
          // Prefix the toast with the tracking ID so the clerk has it
          // visible the moment the request lands — the same string they'll
          // see on the printed barcode slip.
          message: `${reqNo ? reqNo + ' · ' : ''}${itemName} ${
            toClaim
              ? `added to the ${getInsurance(selectedPatient)} claim — ${cfg.requestFreeSuffix.replace(/^requested — /, '')}`
              : Number(chargeAmount) === 0 ? cfg.requestFreeSuffix : cfg.requestSuccessSuffix
          }`,
        });
        loadExistingForPatient(selectedPatient);
      } else {
        setSnackbar({ open: true, severity: 'error', message: serverMsg || `Could not request ${itemName}.` });
      }
    } catch (err) {
      console.error('request failed:', err);
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.response?.data?.message || `Could not request ${item.name}.`,
      });
    } finally {
      setRequestingId(null);
    }
  };

  // ── Delete a pending request, or reverse a paid one ──────────────────
  // The two backend endpoints already encode the business rule: if the
  // charge is still pending (not yet paid) they purge the lab_charge +
  // lab_request + investigation (or other_charge) rows entirely, so it
  // disappears from Accounts and from the worklist. If the patient has
  // already paid, the backend refuses to delete and returns a `reversal`
  // message instead — we surface that to the clerk so they go through
  // Accounts for the refund.
  const handleDelete = async (tab, row) => {
    if (!selectedPatient) return;
    const itemName = row.lab || row.service;
    const rowDate  = (row.date || '').slice(0, 10) || today;
    const isPaid   = String(row.status || '').toLowerCase() === 'paid' && Number(row.amount || 0) > 0;

    const confirm = await Swal.fire({
      title: isPaid ? 'Already paid' : `Delete ${itemName}?`,
      html: isPaid
        ? `<p>This charge has already been settled at Accounts.</p>
           <p>A direct delete isn't allowed — Accounts must process the reversal/refund.
           Continuing will flag the row for reversal, but no money moves until Accounts handles it.</p>`
        : `<p>This will remove <strong>${itemName}</strong> from the request queue so it
           no longer appears at Accounts or on the department worklist. This cannot be undone.</p>`,
      icon: isPaid ? 'info' : 'warning',
      showCancelButton: true,
      confirmButtonText: isPaid ? 'Flag for Accounts reversal' : 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: isPaid ? '#2563EB' : '#ef4444',
    });
    if (!confirm.isConfirmed) return;

    try {
      let resp;
      if (tab === 'lab' || tab === 'scan') {
        // /deleteRequestedLab works for both — lab_charge is shared, and
        // the SQL doesn't filter by investegation, so a scan name hits
        // the same row.
        resp = await axios.post(deleteRequestedLabdb, {
          patientId: selectedPatient.id,
          labName: itemName,
          dat: rowDate,
          currentUser,
        });
      } else {
        resp = await axios.post(deleteOrReverseOtherServicedb, {
          patientId: selectedPatient.id,
          scanName: itemName,           // backend reuses this param name
          dat: rowDate,
          currentUser,
        });
      }

      if (resp.data?.success) {
        // `reversal` is set whenever the backend declined to delete and
        // tagged the row instead. Show that text exactly so the clerk
        // knows the next action lives at Accounts.
        const reversalMsg =
          resp.data?.reversal ||
          (resp.data?.message && /reversal/i.test(resp.data.message) ? resp.data.message : null);

        if (reversalMsg) {
          await Swal.fire({
            title: 'Reversal required at Accounts',
            text: reversalMsg,
            icon: 'info',
            confirmButtonText: 'OK',
          });
        } else {
          setSnackbar({ open: true, severity: 'success', message: `${itemName} removed.` });
        }
        // Refresh the on-file lists so the row disappears from the sidebar.
        loadExistingForPatient(selectedPatient);
        // Also drop it from the session-requests strip if it's there.
        setSessionRequests(prev => prev.filter(r => !(r.tab === tab && r.itemName === itemName)));
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: resp.data?.message || `Could not delete ${itemName}.`,
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.response?.data?.message || `Delete failed for ${itemName}.`,
      });
    }
  };

  // ── Change referring doctor (same-day correction) ────────────────────
  // Lets the clerk fix a mis-assigned referring doctor on a row that's
  // still dated today. The backend enforces the day-window; this state
  // just drives the dialog.
  const [docEdit, setDocEdit] = useState({
    open: false,
    tab: 'lab',
    row: null,
    selected: null,    // option object from doctorOptions
    saving: false,
  });
  const openDoctorEdit = (tab, row) => {
    // Pre-select whatever doc the row currently has — if it matches an
    // option in the loaded list we surface that, otherwise fall back to
    // a synthetic option so the field isn't blank on first paint.
    const current = row.requestedBy || row.doc_name || '';
    const match = doctorOptions.find(o =>
      String(o.name || '').toLowerCase() === String(current).toLowerCase()
    );
    setDocEdit({
      open: true,
      tab,
      row,
      selected: match || (current ? { id: '__current__', name: current } : null),
      saving: false,
    });
  };
  const closeDoctorEdit = () => {
    if (docEdit.saving) return;
    setDocEdit(s => ({ ...s, open: false }));
  };
  const saveDoctorEdit = async () => {
    if (!docEdit.row || !docEdit.selected) {
      setSnackbar({ open: true, severity: 'error', message: 'Pick a doctor first.' });
      return;
    }
    const reqNo = docEdit.row.req_no;
    if (!reqNo) {
      setSnackbar({
        open: true, severity: 'error',
        message: 'This row has no tracking ID, so it can\'t be re-assigned.',
      });
      return;
    }
    setDocEdit(s => ({ ...s, saving: true }));
    try {
      const { data } = await axios.put(
        changeRequestDoctorUrl(docEdit.tab, reqNo),
        { newDoctor: docEdit.selected.name, currentUser },
        { timeout: 12000 }
      );
      if (data?.success) {
        setSnackbar({
          open: true, severity: 'success',
          message: `Referring doctor updated to ${docEdit.selected.name}.`,
        });
        setDocEdit({ open: false, tab: 'lab', row: null, selected: null, saving: false });
        if (selectedPatient) loadExistingForPatient(selectedPatient);
      } else {
        setSnackbar({
          open: true, severity: 'error',
          message: data?.message || 'Could not update referring doctor.',
        });
        setDocEdit(s => ({ ...s, saving: false }));
      }
    } catch (err) {
      // 409 from the backend means the row is no longer same-day —
      // surface that as the actionable message.
      const msg = err.response?.data?.message
        || (err.response?.status === 409
            ? 'Doctor can only be changed on the same day the request was filed.'
            : 'Could not update referring doctor.');
      setSnackbar({ open: true, severity: 'error', message: msg });
      setDocEdit(s => ({ ...s, saving: false }));
    }
  };

  // ── Derived lists ─────────────────────────────────────────────────────
  const filteredPatients = useMemo(() => {
    const q = patientSearch.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter(p =>
      String(p.id || '').toLowerCase().includes(q) ||
      String(p.fullName || '').toLowerCase().includes(q) ||
      String(p.tel || '').toLowerCase().includes(q)
    );
  }, [patients, patientSearch]);

  const filteredCatalogue = useMemo(() => {
    const q = (searches[activeTab] || '').trim().toLowerCase();
    const rows = catalogue[activeTab] || [];
    if (!q) return rows;
    return rows.filter(r => String(r.name || '').toLowerCase().includes(q));
  }, [catalogue, searches, activeTab]);

  const cfg = TABS[activeTab];

  // ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: t.pageBg, p: { xs: 1.5, md: 3 } }}>
      {/* ── Page header ─────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          mb: 2,
          borderRadius: 2,
          bgcolor: t.surface,
          border: `1px solid ${t.borderSubtle}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar sx={{
          bgcolor: alpha(cfg.accent, 0.16),
          color: cfg.accent,
          width: 44, height: 44,
          border: `1px solid ${alpha(cfg.accent, 0.32)}`,
        }}>
          <RequestIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 800, letterSpacing: '1.4px',
            textTransform: 'uppercase', color: cfg.accent, lineHeight: 1,
          }}>
            Medical Records
          </Typography>
          <Typography sx={{
            fontSize: { xs: '1.05rem', md: '1.2rem' }, fontWeight: 700,
            color: t.text, mt: 0.25,
          }}>
            Request Services
            <Typography component="span" sx={{
              fontSize: '0.78rem', fontWeight: 500, color: t.textMuted, ml: 1.25,
            }}>
              Send labs, scans or other services to Accounts on a patient's behalf
            </Typography>
          </Typography>
        </Box>

        {selectedPatient && (
          presetPatient ? (
            // Dialog flow — show a Close button instead of "Back to list"
            // because there's no list to go back to.
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: t.textMuted,
                border: `1px solid ${t.borderStrong}`,
                '&:hover': { bgcolor: t.rowHover, color: t.text },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToList}
              sx={{
                textTransform: 'none', fontWeight: 600,
                color: t.textMuted, borderColor: t.borderStrong,
                '&:hover': { bgcolor: t.rowHover, borderColor: t.borderStrong },
              }}
              variant="outlined"
              size="small"
            >
              Back to patient list
            </Button>
          )
        )}
      </Paper>

      {!selectedPatient ? (
        <PatientPicker
          t={t} cfg={cfg}
          filter={filter} setFilter={setFilter}
          patientSearch={patientSearch} setPatientSearch={setPatientSearch}
          filteredPatients={filteredPatients}
          patientsLoading={patientsLoading}
          onRefresh={loadPatients}
          onSelect={handleSelectPatient}
        />
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' } }}>
          <Box>
            <PatientHeader t={t} patient={selectedPatient} />

            {/* ── Referring doctor selector ──────────────────────────────
                Required before any request fires. Onboarded doctors are
                fetched once and merged behind a pinned "Walk-in" option
                for patients who arrived without a referral. Whatever is
                selected here becomes `doc_name` on every lab_charge /
                lab_request / investigation / other_charge row this
                session writes, which is the column commission reports
                key off downstream. */}
            <ReferringDoctorBar
              t={t}
              cfg={cfg}
              options={doctorOptions}
              loading={doctorsLoading}
              value={referringDoctor}
              onChange={setReferringDoctor}
              walkIn={WALK_IN}
              onRefresh={loadDoctors}
            />

            {/* ── Tab switcher ──────────────────────────────────────── */}
            <Paper elevation={0} sx={{
              p: 0.75, mb: 2,
              borderRadius: 2,
              bgcolor: t.surface,
              border: `1px solid ${t.borderSubtle}`,
            }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="fullWidth"
                TabIndicatorProps={{ sx: { display: 'none' } }}
                sx={{
                  minHeight: 'auto',
                  '& .MuiTab-root': {
                    textTransform: 'none', fontWeight: 700, fontSize: '0.86rem',
                    color: t.textMuted,
                    borderRadius: 1.5,
                    minHeight: 38,
                    gap: 0.75,
                    transition: 'background 120ms ease, color 120ms ease',
                  },
                  '& .MuiTab-root.Mui-selected': {
                    color: TABS[activeTab].accent,
                    bgcolor: alpha(TABS[activeTab].accent, 0.14),
                  },
                  '& .MuiTab-root:hover': { color: t.text },
                }}
              >
                {Object.values(TABS).map(c => (
                  <Tab
                    key={c.key}
                    value={c.key}
                    iconPosition="start"
                    icon={c.icon}
                    label={c.label}
                  />
                ))}
              </Tabs>
            </Paper>

            <Catalogue
              t={t}
              cfg={cfg}
              rows={filteredCatalogue}
              search={searches[activeTab] || ''}
              setSearch={(v) => setSearches(s => ({ ...s, [activeTab]: v }))}
              loading={catLoading[activeTab]}
              today={today}
              requestingId={requestingId}
              onRequest={handleRequest}
              isClaimed={(item) => routeToClaim(activeTab, item)}
              isRequested={(item) =>
                sessionRequests.some(r => r.tab === activeTab && r.itemName === item.name) ||
                (existing[activeTab] || []).some(r => (r.lab || r.service) === item.name)
              }
            />
          </Box>
          {/* Right-rail is sticky + self-scrolling. As session requests
              and "on file today" grow, only this column scrolls — the
              patient header, doctor bar, tab switcher and catalogue stay
              put. `alignSelf: 'flex-start'` keeps the rail from being
              stretched to the catalogue's height by the grid. */}
          <Box
            sx={{
              position: { lg: 'sticky' },
              top: { lg: 16 },
              alignSelf: 'flex-start',
              maxHeight: { lg: 'calc(100vh - 32px)' },
              overflowY: { lg: 'auto' },
              pr: { lg: 0.5 },             // breathing room for the scrollbar
              // Slim, theme-aware scrollbar so the rail doesn't fight the page.
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: t.borderStrong,
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: t.textMuted },
              scrollbarWidth: 'thin',
              scrollbarColor: `${t.borderStrong} transparent`,
            }}
          >
            <RequestSidebar
              t={t}
              sessionRequests={sessionRequests}
              existing={existing}
              currentUser={currentUser}
              onDelete={handleDelete}
              onEditDoctor={openDoctorEdit}
            />
          </Box>
        </Box>
      )}

      {/* ── Change referring doctor (same-day) ──────────────────────── */}
      <Dialog
        open={docEdit.open}
        onClose={closeDoctorEdit}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { bgcolor: t.surface, color: t.text, borderRadius: 2, border: `1px solid ${t.borderSubtle}` },
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${t.borderSubtle}`, fontWeight: 800, fontSize: '1rem' }}>
          Change referring doctor
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1.5 }}>
          {docEdit.row && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', color: t.textMuted, letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 800 }}>
                  Request
                </Typography>
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: t.text, mt: 0.25 }}>
                  {docEdit.row.lab || docEdit.row.service}
                </Typography>
                {docEdit.row.req_no && (
                  <Typography sx={{ fontSize: '0.74rem', fontFamily: 'monospace', color: t.accent.blue, fontWeight: 700, mt: 0.25 }}>
                    {docEdit.row.req_no}
                  </Typography>
                )}
                {docEdit.row.requestedBy && (
                  <Typography sx={{ fontSize: '0.74rem', color: t.textDim, mt: 0.5 }}>
                    Currently assigned to <strong style={{ color: t.textMuted }}>{docEdit.row.requestedBy}</strong>
                  </Typography>
                )}
              </Box>
              <Autocomplete
                size="small"
                options={doctorOptions}
                getOptionLabel={(o) => o?.name || ''}
                isOptionEqualToValue={(a, b) => (a?.id ?? a?.name) === (b?.id ?? b?.name)}
                value={docEdit.selected}
                onChange={(_, v) => setDocEdit(s => ({ ...s, selected: v }))}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {option._walkin
                      ? <WalkInIcon sx={{ fontSize: 16, color: t.accent.amber }} />
                      : <DoctorIcon  sx={{ fontSize: 16, color: t.textMuted }} />}
                    <Box>
                      <Typography sx={{ fontSize: '0.85rem', color: t.text, fontWeight: 600 }}>
                        {option._walkin ? 'Walk-in (no referring doctor)' : `Dr. ${option.name}`}
                      </Typography>
                      {option.facility_name && (
                        <Typography sx={{ fontSize: '0.7rem', color: t.textDim }}>
                          {option.facility_name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Reassign to"
                    placeholder="Pick onboarded doctor or Walk-in…"
                  />
                )}
              />
              <Typography sx={{ fontSize: '0.72rem', color: t.textDim, lineHeight: 1.4 }}>
                Allowed only on the same day the request was filed. After that, a reversal at Accounts is required.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${t.borderSubtle}` }}>
          <Button onClick={closeDoctorEdit} disabled={docEdit.saving} sx={{ color: t.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={saveDoctorEdit}
            disabled={docEdit.saving || !docEdit.selected}
            sx={{
              textTransform: 'none', fontWeight: 700, color: '#fff',
              background: t.accent.blue,
              px: 2.5, py: 0.8, borderRadius: 1.5,
              '&:hover': { background: t.accent.blue, opacity: 0.92 },
            }}
            startIcon={docEdit.saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          >
            Save change
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Patient picker — shown before a patient is selected
// ──────────────────────────────────────────────────────────────────────
function PatientPicker({
  t, cfg, filter, setFilter, patientSearch, setPatientSearch,
  filteredPatients, patientsLoading, onRefresh, onSelect,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2 },
        borderRadius: 2,
        bgcolor: t.surface,
        border: `1px solid ${t.borderSubtle}`,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          fullWidth
          size="small"
          value={patientSearch}
          onChange={(e) => setPatientSearch(e.target.value)}
          placeholder="Search by name, patient ID, or phone…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: t.textMuted, fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: patientSearch && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setPatientSearch('')}>
                  <ClearIcon fontSize="small" sx={{ color: t.textMuted }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5, bgcolor: t.fieldBg, color: t.text,
              '& fieldset': { borderColor: t.borderSubtle },
            },
          }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={filter}
          onChange={(_, v) => v && setFilter(v)}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontWeight: 600, fontSize: '0.82rem',
              color: t.textMuted, borderColor: t.borderSubtle, px: 1.75,
              '&.Mui-selected': {
                bgcolor: alpha(cfg.accent, 0.16),
                color: cfg.accent,
                borderColor: alpha(cfg.accent, 0.4),
                '&:hover': { bgcolor: alpha(cfg.accent, 0.22) },
              },
            },
          }}
        >
          {FILTERS.map(f => (
            <ToggleButton key={f.value} value={f.value}>{f.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Refresh list">
          <IconButton
            onClick={onRefresh}
            size="small"
            sx={{ border: `1px solid ${t.borderSubtle}`, color: t.textMuted }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider sx={{ borderColor: t.borderSubtle, mb: 1.5 }} />

      {patientsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} sx={{ color: cfg.accent }} />
        </Box>
      ) : filteredPatients.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <PersonIcon sx={{ fontSize: 48, color: t.textDim, mb: 1 }} />
          <Typography sx={{ color: t.textMuted, fontWeight: 600 }}>
            {patientSearch ? 'No patients match that search.' : 'No active patients to show.'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.25}>
          {filteredPatients.map((p) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${t.borderSubtle}`,
                  bgcolor: t.surfaceAlt,
                  transition: 'border-color 120ms ease, transform 120ms ease',
                  '&:hover': {
                    borderColor: alpha(cfg.accent, 0.5),
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <CardActionArea onClick={() => onSelect(p)} sx={{ p: 1.25 }}>
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Avatar sx={{
                        bgcolor: p.state === 'inPatient'
                          ? alpha(t.accent.blue, 0.18)
                          : alpha(cfg.accent, 0.16),
                        color: p.state === 'inPatient' ? t.accent.blue : cfg.accent,
                        width: 40, height: 40, fontWeight: 700,
                      }}>
                        {String(p.fullName || '?').trim().charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          fontWeight: 700, color: t.text, fontSize: '0.92rem',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {p.fullName || '—'}
                        </Typography>
                        <Typography sx={{ color: t.textMuted, fontSize: '0.72rem' }}>
                          {p.id} · {p.gender || '—'} · {computeAge(p.dob)?.label || (p.age ? `${p.age}y` : '—')}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={p.insurance || p.type || 'Non Insured'}
                        sx={smallChipSx(t, t.accent.amber)}
                      />
                      <Chip
                        size="small"
                        label={p.state === 'inPatient' ? 'Inpatient' : 'Outpatient'}
                        sx={smallChipSx(t, p.state === 'inPatient' ? t.accent.blue : cfg.accent)}
                      />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Selected-patient header
// ──────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────
//  Referring-doctor selector — onboarded doctors + a pinned "Walk-in"
//  fallback. Sits between the patient header and the tab switcher so the
//  clerk picks the clinician once and every request in the session is
//  tagged with that name on `doc_name`.
// ──────────────────────────────────────────────────────────────────────
function ReferringDoctorBar({ t, cfg, options, loading, value, onChange, walkIn, onRefresh }) {
  // Label option: pinned Walk-in shows the walker icon + bold label,
  // onboarded doctors show their name + a faint facility line so the
  // clerk picks the right one when names repeat across facilities.
  const isWalkIn = value && value._walkin;
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1, md: 1.25 },
        mb: 2,
        borderRadius: 2,
        bgcolor: t.surface,
        // Outline shifts to the accent of the active tab when no doctor
        // is picked yet — visual nudge that this gate must be cleared
        // before any Request button will fire.
        border: `1px solid ${value
          ? t.borderSubtle
          : alpha(cfg.accent, 0.45)}`,
        display: 'flex', alignItems: 'center', gap: 1.25,
      }}
    >
      <Avatar sx={{
        bgcolor: alpha(cfg.accent, 0.16),
        color: cfg.accent,
        width: 36, height: 36,
        border: `1px solid ${alpha(cfg.accent, 0.32)}`,
      }}>
        {isWalkIn ? <WalkInIcon sx={{ fontSize: 20 }} /> : <DoctorIcon sx={{ fontSize: 20 }} />}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.66rem', fontWeight: 800, letterSpacing: '1.2px',
          textTransform: 'uppercase', color: t.textMuted, lineHeight: 1, mb: 0.5,
        }}>
          Requesting on behalf of *
        </Typography>
        <Autocomplete
          size="small"
          options={options}
          value={value}
          loading={loading}
          onChange={(_, v) => onChange(v)}
          getOptionLabel={(o) => (o?._walkin ? 'Walk-in' : (o?.name || ''))}
          isOptionEqualToValue={(o, v) => (o?.id || o?.name) === (v?.id || v?.name)}
          // Pinned-walk-in stays on top thanks to the original array order
          // we built (WALK_IN first), so no custom sorter is needed.
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id || option.name} sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              borderBottom: option._walkin ? `1px dashed ${t.borderSubtle}` : 'none',
            }}>
              {option._walkin
                ? <WalkInIcon sx={{ fontSize: 18, color: cfg.accent }} />
                : <DoctorIcon  sx={{ fontSize: 18, color: t.textMuted }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: option._walkin ? 800 : 600, color: t.text, fontSize: '0.86rem' }}>
                  {option._walkin ? 'Walk-in (no referring doctor)' : `Dr. ${option.name}`}
                </Typography>
                {!option._walkin && (option.specialization || option.facility_name) && (
                  <Typography sx={{ fontSize: '0.72rem', color: t.textMuted }}>
                    {[option.specialization, option.facility_name].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search onboarded doctors or pick Walk-in…"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5, bgcolor: t.fieldBg, color: t.text,
                  '& fieldset': { borderColor: t.borderSubtle },
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={14} sx={{ color: cfg.accent, mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ '& .MuiAutocomplete-popupIndicator': { color: t.textMuted } }}
        />
      </Box>
      {value && (
        <Chip
          size="small"
          icon={isWalkIn ? <WalkInIcon sx={{ fontSize: 14 }} /> : <DoctorIcon sx={{ fontSize: 14 }} />}
          label={isWalkIn ? 'Walk-in' : `Dr. ${value.name}`}
          sx={{
            height: 26, fontWeight: 700, fontSize: '0.72rem',
            bgcolor: alpha(cfg.accent, 0.16), color: cfg.accent,
            border: `1px solid ${alpha(cfg.accent, 0.32)}`,
          }}
        />
      )}
      <Tooltip title="Refresh doctor list">
        <span>
          <IconButton size="small" onClick={onRefresh}
            sx={{ color: t.textMuted, border: `1px solid ${t.borderSubtle}` }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Paper>
  );
}

function PatientHeader({ t, patient }) {
  const ageInfo = computeAge(patient.dob);
  const ageLabel = ageInfo?.label || (patient.age ? `${patient.age}y` : '—');
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2 },
        mb: 2,
        borderRadius: 2,
        bgcolor: t.surface,
        border: `1px solid ${t.borderSubtle}`,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}
    >
      <Avatar sx={{
        bgcolor: alpha(t.accent.blue, 0.16),
        color: t.accent.blue,
        width: 48, height: 48, fontWeight: 700, fontSize: '1.1rem',
        border: `1px solid ${alpha(t.accent.blue, 0.32)}`,
      }}>
        {String(patient.fullName || '?').trim().charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: t.text, fontSize: '1.05rem' }}>
          {patient.fullName}
          <Typography component="span" sx={{ ml: 1, fontWeight: 500, color: t.textMuted, fontSize: '0.85rem' }}>
            · ID {patient.id}
          </Typography>
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
          <Chip size="small" label={patient.gender || '—'} sx={chipSx(t, t.accent.blue)} />
          <Chip size="small" label={ageLabel}              sx={chipSx(t, t.accent.emerald)} />
          {/* Insurance scheme — the value the lab/scan catalogue keys off.
              Shown explicitly so the clerk can confirm which scheme tariff
              they're requesting at before sending. */}
          <Chip
            size="small"
            label={`Insurance: ${patient.insurance || patient.type || 'Non Insured'}`}
            sx={chipSx(t, t.accent.amber)}
          />
          {/* Membership number — surfaced when the scheme isn't plain
              cash so the clerk doesn't need to flip back to the records
              row to copy it. */}
          {(patient.Insurance || patient.membershipNo) && (
            <Chip
              size="small"
              label={`Member #: ${patient.Insurance || patient.membershipNo}`}
              sx={chipSx(t, t.accent.amber)}
            />
          )}
          <Chip
            size="small"
            label={patient.state === 'inPatient' ? 'Inpatient' : 'Outpatient'}
            sx={chipSx(t, patient.state === 'inPatient' ? t.accent.blue : t.accent.green)}
          />
        </Stack>
      </Box>
    </Paper>
  );
}

function chipSx(t, color) {
  return {
    height: 22, fontSize: '0.7rem', fontWeight: 700,
    bgcolor: alpha(color, 0.16), color,
    border: `1px solid ${alpha(color, 0.28)}`,
  };
}
function smallChipSx(t, color) {
  return {
    height: 20, fontSize: '0.65rem', fontWeight: 700,
    bgcolor: alpha(color, 0.16), color,
  };
}

// ──────────────────────────────────────────────────────────────────────
//  Dialog wrapper — used by the per-row "Request" action on the Records
//  list so the clerk can open the same Labs/Scans/Other Services tabbed
//  workspace as a modal for the specific patient they clicked. Renders
//  fullscreen-ish (md width, ~92vh tall) so all three tabs and the
//  sidebar stay usable without scrolling the page underneath.
// ──────────────────────────────────────────────────────────────────────
export function RequestServicesDialog({ open, patient, onClose }) {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';

  // The previous version painted the dialog the same colour as the page
  // underneath, so the modal had no perceivable edge in dark mode and the
  // clerk couldn't tell what was the dialog vs the background. We fix that
  // with three layered cues so the separation reads at a glance in both
  // light and dark mode:
  //
  //   1. A blurred, heavy-darkened backdrop — clearly dims the underlying
  //      records page so the eye lands on the modal.
  //   2. A bright accent border + indigo glow on the dialog frame — turns
  //      the modal into a distinctly raised card no matter the surface.
  //   3. A different bgcolor for the dialog frame than the workspace
  //      inside (which keeps using the page palette) so there's a visible
  //      inset gap between them.
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      // Backdrop: heavy dim + blur. MUI's default 0.5 alpha is invisible
      // when the page is already near-black; 0.78 + 4px blur reads clearly.
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: isDark
              ? 'rgba(0,0,0,0.78)'
              : 'rgba(15,23,42,0.55)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          // Slightly inset frame around the workspace. Dark mode: a touch
          // lighter than pure black so it reads as a raised plate; light
          // mode: clean white so the workspace's grey page colour stands
          // out as the inner surface.
          bgcolor: isDark ? '#1A1A24' : '#FFFFFF',
          backgroundImage: 'none',
          borderRadius: 3,
          height: '92vh',
          // Accent ring + drop shadow — gives the modal a clear physical
          // edge and a soft branded glow.
          border: `1px solid ${isDark ? 'rgba(99,102,241,0.45)' : 'rgba(99,102,241,0.30)'}`,
          boxShadow: isDark
            ? '0 28px 64px rgba(0,0,0,0.65), 0 0 0 1px rgba(99,102,241,0.12), 0 0 48px rgba(99,102,241,0.18)'
            : '0 28px 64px rgba(15,23,42,0.18), 0 0 0 1px rgba(99,102,241,0.10)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Padding around DialogContent gives the workspace a visible inset
          gap from the dialog frame so the two layers don't blend. */}
      <DialogContent sx={{ p: { xs: 0.75, md: 1 }, overflow: 'auto', bgcolor: 'transparent' }}>
        {patient && <RequestServices patient={patient} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Catalogue + per-row Request button (shared across all three tabs)
// ──────────────────────────────────────────────────────────────────────
function Catalogue({
  t, cfg, rows, search, setSearch, today, loading,
  requestingId, onRequest, isRequested, isClaimed = () => false,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        bgcolor: t.surface,
        border: `1px solid ${t.borderSubtle}`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: { xs: 1.5, md: 2 }, borderBottom: `1px solid ${t.borderSubtle}` }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={cfg.searchPlaceholder}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: t.textMuted, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5, bgcolor: t.fieldBg, color: t.text,
                '& fieldset': { borderColor: t.borderSubtle },
              },
            }}
          />
          <Chip
            icon={<HospitalIcon sx={{ fontSize: 16 }} />}
            label={moment(today).format('ddd, MMM D, YYYY')}
            sx={{
              height: 36, px: 1, borderRadius: 1.5, fontWeight: 700,
              bgcolor: alpha(cfg.accent, 0.12),
              color: cfg.accent,
              border: `1px solid ${alpha(cfg.accent, 0.32)}`,
              '& .MuiChip-icon': { color: cfg.accent },
            }}
          />
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={26} sx={{ color: cfg.accent }} />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <RequestIcon sx={{ fontSize: 44, color: t.textDim, mb: 1 }} />
          <Typography sx={{ color: t.textMuted, fontWeight: 600 }}>
            {search ? 'No items match that search.' : cfg.emptyLabel}
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {[cfg.columnLabel, 'Code', 'Amount', 'Status', ''].map((h, i) => (
                  <TableCell
                    key={h + i}
                    align={i >= 2 ? (i === 4 ? 'center' : 'right') : 'left'}
                    sx={{
                      bgcolor: t.surfaceAlt,
                      color: t.textMuted,
                      borderBottom: `1px solid ${t.borderSubtle}`,
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((item, idx) => {
                const key  = `${cfg.key}-${item.code || item.id || idx}-${idx}`;
                const done = isRequested(item);
                const busy = requestingId === key;
                const claim = isClaimed(item);
                return (
                  <TableRow
                    key={key}
                    sx={{
                      '&:hover': { bgcolor: t.rowHover },
                      ...(done && { bgcolor: alpha(t.accent.emerald, 0.08) }),
                    }}
                  >
                    <TableCell sx={{ color: t.text, fontWeight: 600, borderColor: t.borderSubtle, textTransform: 'uppercase' }}>
                      {item.name}
                    </TableCell>
                    <TableCell sx={{ color: t.textMuted, fontFamily: 'monospace', fontSize: '0.78rem', borderColor: t.borderSubtle }}>
                      {item.code || '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: t.text, fontWeight: 700, borderColor: t.borderSubtle }}>
                      {claim
                        ? formatWithOutSom(claimTariff(cfg.key, item))
                        : Number(item.amount) === 0
                          ? <Chip size="small" label="Free" sx={chipSx(t, t.accent.emerald)} />
                          : formatWithOutSom(item.amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: t.borderSubtle }}>
                      {done ? (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                          size="small"
                          label="Requested"
                          sx={chipSx(t, t.accent.emerald)}
                        />
                      ) : claim ? (
                        <Tooltip title="Billed to the insurer on the patient's claim — nothing to pay at Accounts">
                          <Chip size="small" label="Insurance claim" sx={chipSx(t, t.accent.blue)} />
                        </Tooltip>
                      ) : Number(item.amount) === 0 ? (
                        <Chip size="small" label="Insured" sx={chipSx(t, t.accent.blue)} />
                      ) : (
                        <Chip
                          icon={<HourglassIcon sx={{ fontSize: 14 }} />}
                          size="small"
                          label="Pay at Accounts"
                          sx={chipSx(t, t.accent.amber)}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ borderColor: t.borderSubtle }}>
                      {busy ? (
                        <CircularProgress size={20} sx={{ color: cfg.accent }} />
                      ) : (
                        <Tooltip title={done ? 'Request again' : 'Request'}>
                          <IconButton
                            size="small"
                            onClick={() => onRequest(item, key)}
                            sx={{
                              bgcolor: alpha(cfg.accent, 0.12),
                              color: cfg.accent,
                              '&:hover': { bgcolor: alpha(cfg.accent, 0.22) },
                            }}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Right-rail summary — session activity + how-this-works
// ──────────────────────────────────────────────────────────────────────
function RequestSidebar({ t, sessionRequests, existing, currentUser, onDelete, onEditDoctor }) {
  const totalExisting =
    (existing.lab?.length || 0) +
    (existing.scan?.length || 0) +
    (existing.other?.length || 0);

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          p: 1.75, borderRadius: 2,
          bgcolor: t.surface, border: `1px solid ${t.borderSubtle}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <InfoIcon sx={{ color: t.accent.blue, fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: t.text, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
            How this works
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: '0.78rem', color: t.textMuted, lineHeight: 1.55 }}>
          For insured patients, labs and scans go on the patient's{' '}
          <strong style={{ color: t.text }}>insurance claim</strong> and appear on the
          worklist immediately — nothing is sent to Accounts. Non-insured items (and Other
          Services) are routed to <strong style={{ color: t.text }}>Accounts</strong> for
          payment first and reach the lab / scan / billing panel once paid.
        </Typography>
        {currentUser && (
          <Typography sx={{ fontSize: '0.72rem', color: t.textDim, mt: 1.25 }}>
            Requesting as <strong style={{ color: t.textMuted }}>{currentUser}</strong>
          </Typography>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 1.75, borderRadius: 2,
          bgcolor: t.surface, border: `1px solid ${t.borderSubtle}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: t.text, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
            Requested this session
          </Typography>
          <Chip size="small" label={sessionRequests.length} sx={chipSx(t, t.accent.blue)} />
        </Stack>
        {sessionRequests.length === 0 ? (
          <Typography sx={{ fontSize: '0.78rem', color: t.textDim }}>
            Nothing requested yet. Click <SendIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> on any row to start.
          </Typography>
        ) : (
          <Stack divider={<Divider sx={{ borderColor: t.borderSubtle }} />} spacing={0.75}>
            {sessionRequests.map((r, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '0.82rem', color: t.text, fontWeight: 600,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {r.itemName}
                  </Typography>
                  {r.reqNo && (
                    // Mono-spaced tracking ID — what the clerk reads off
                    // the printed barcode slip when handing it to the
                    // patient or the department.
                    <Typography sx={{
                      fontSize: '0.7rem', fontFamily: 'monospace',
                      color: TABS[r.tab]?.accent || t.accent.blue,
                      fontWeight: 700, mt: 0.25,
                    }}>
                      {r.reqNo}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '0.7rem', color: t.textMuted }}>
                    {moment(r.at).format('HH:mm')} · {TABS[r.tab]?.label} · {r.claimed ? 'Insurance claim' : r.insured ? 'Insured' : formatWithOutSom(r.amount)}
                  </Typography>
                  {r.referringDoctor && r.referringDoctor !== '—' && (
                    <Typography sx={{ fontSize: '0.68rem', color: t.textDim, mt: 0.25 }}>
                      <DoctorIcon sx={{ fontSize: 11, mr: 0.5, verticalAlign: 'middle' }} />
                      {r.referringDoctor === 'Walk-in' ? 'Walk-in' : `Dr. ${r.referringDoctor}`}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  label={r.insured ? 'Worklist' : 'Pending'}
                  sx={chipSx(t, r.insured ? t.accent.emerald : t.accent.amber)}
                />
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {totalExisting > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 1.75, borderRadius: 2,
            bgcolor: t.surface, border: `1px solid ${t.borderSubtle}`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: t.text, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
              Already on file today
            </Typography>
            <Chip
              size="small"
              icon={<HospitalIcon sx={{ fontSize: 14 }} />}
              label={totalExisting}
              sx={chipSx(t, t.accent.blue)}
            />
          </Stack>
          <Stack spacing={1.25}>
            {['lab', 'scan', 'other'].map(tab => {
              const list = existing[tab] || [];
              if (list.length === 0) return null;
              return (
                <Box key={tab}>
                  <Typography sx={{
                    fontSize: '0.68rem', fontWeight: 800,
                    color: TABS[tab].accent, letterSpacing: '0.6px',
                    textTransform: 'uppercase', mb: 0.5,
                  }}>
                    {TABS[tab].label} ({list.length})
                  </Typography>
                  <Stack spacing={0.5}>
                    {list.slice(0, 6).map((r, i) => {
                      // Paid + amount > 0 means money has moved — the
                      // backend won't actually delete, it'll only tag the
                      // row for Accounts reversal. We surface that here
                      // by switching the icon/colour so the clerk has a
                      // hint before they click.
                      const isPaid = String(r.status || '').toLowerCase() === 'paid'
                        && Number(r.amount || 0) > 0;
                      const itemName = r.lab || r.service;
                      return (
                        <Box
                          key={i}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            pl: 0.75,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: '0.74rem', color: t.textMuted,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}
                            >
                              · {itemName}
                            </Typography>
                            {r.req_no && (
                              <Typography sx={{
                                fontSize: '0.66rem', fontFamily: 'monospace',
                                color: TABS[tab].accent, fontWeight: 700,
                                pl: 1,
                              }}>
                                {r.req_no}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            size="small"
                            label={isPaid ? 'Paid' : 'Pending'}
                            sx={{
                              height: 18, fontSize: '0.62rem', fontWeight: 700,
                              bgcolor: alpha(isPaid ? t.accent.emerald : t.accent.amber, 0.16),
                              color: isPaid ? t.accent.emerald : t.accent.amber,
                            }}
                          />
                          {/* Same-day correction of the referring doctor.
                              Hidden when the row has no tracking ID (req_no
                              is what the backend keys the update by). */}
                          {r.req_no && onEditDoctor && (
                            <Tooltip title={`Change referring doctor${r.requestedBy ? ` (currently ${r.requestedBy})` : ''}`}>
                              <IconButton
                                size="small"
                                onClick={() => onEditDoctor(tab, r)}
                                sx={{
                                  p: 0.25,
                                  color: t.accent.blue,
                                  '&:hover': { bgcolor: alpha(t.accent.blue, 0.14) },
                                }}
                              >
                                <EditDoctorIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip
                            title={isPaid
                              ? 'Already paid — flag for Accounts reversal'
                              : 'Delete this pending request'}
                          >
                            <IconButton
                              size="small"
                              onClick={() => onDelete && onDelete(tab, r)}
                              sx={{
                                p: 0.25,
                                color: isPaid ? t.accent.blue : t.accent.danger,
                                '&:hover': {
                                  bgcolor: alpha(isPaid ? t.accent.blue : t.accent.danger, 0.14),
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    })}
                    {list.length > 6 && (
                      <Typography sx={{ fontSize: '0.7rem', color: t.textDim, pl: 0.75 }}>
                        …and {list.length - 6} more
                      </Typography>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
